// src/components/ErrorDisplayModal.js
import React from 'react';
import Modal from 'react-modal';
import { ERROR_CODES, ERROR_MESSAGES } from '../constants/errors';
import styles from './ErrorDisplayModal.module.css';
import { useNavigate } from 'react-router-dom'; // Import useNavigate

// Set this in your main App.js for accessibility
// Modal.setAppElement('#root');

function ErrorDisplayModal({ error, clearError }) {
    const navigate = useNavigate(); // Hook for navigation
    console.log("[ErrorDisplayModal LOG 1] Component rendered. Raw 'error' prop:", error);
    
    if (!error) {
        return null;
    }

    // --- REFACTORED ERROR PARSING LOGIC ---
    // This new logic correctly handles all known error formats:
    // 1. New structured error object: { message, code, data }
    // 2. Old structured error object: { error, message }
    // 3. Simple string errors
    // 4. Standard JavaScript Error objects: new Error("message")
    
    let errorsToDisplay = [];
    console.log("[ErrorDisplayModal LOG 2] Starting error parsing logic. Type of error is:", typeof error);
    if (typeof error === 'object' && error !== null) {
        if (error.code === 'auth/network-request-failed') {
            console.log("[ErrorDisplayModal LOG 2a] Matched: Firebase Network Error");
            errorsToDisplay.push({
                code: error.code,
                message: "Your network connection was lost. Please check your internet connection and try again."
            });
        } else if (error.code && error.message) {
            console.log("[ErrorDisplayModal LOG 2a] Matched: New structured object {code, message}");
            // This is our new, preferred structured error format from the context
            errorsToDisplay.push({ code: error.code, message: error.message });
            
            // Also check for multiple sub-errors inside the 'data' key, if it exists
            if (error.data && Array.isArray(error.data.errors)) {
                errorsToDisplay.push(...error.data.errors);
            }
        } else if (error.error && error.message) {
            console.log("[ErrorDisplayModal LOG 2b] Matched: Old structured object {error, message}");
            // Backward compatibility for old format: { error: 'CODE', message: '...' }
            errorsToDisplay.push({ code: error.error, message: error.message });
        } else if (error.message) {
            console.log("[ErrorDisplayModal LOG 2c] Matched: Generic object with .message property");
            // Handle standard JS Error objects or objects with only a message
            errorsToDisplay.push({ code: null, message: error.message });
        } else {
            console.log("[ErrorDisplayModal LOG 2d] Matched: Fallback for unknown object format");
            // Fallback for an unknown object format
            errorsToDisplay.push({ code: null, message: "An unexpected error occurred." });
        }
    } else if (typeof error === 'string') {
        // Handle simple string errors
         console.log("[ErrorDisplayModal LOG 2e] Matched: Simple string error");
        let detectedCode = null;
        for (const key in ERROR_MESSAGES) {
            if (ERROR_MESSAGES[key] === error) {
                detectedCode = key;
                break;
            }
        }
        errorsToDisplay.push({ code: detectedCode, message: error });
    } else {
        console.log("[ErrorDisplayModal LOG 2f] Matched: Fallback for unknown error type");
        // Final fallback for any other unexpected type
        errorsToDisplay.push({ code: null, message: "An unknown error occurred. Please try again." });
    }
    // --- END OF REFACTORED LOGIC ---

    console.log("[ErrorDisplayModal LOG 3] After parsing, 'errorsToDisplay' array is:", JSON.stringify(errorsToDisplay, null, 2));
    // Clean up messages to ensure they are always strings
    errorsToDisplay = errorsToDisplay.map(err => ({
        ...err,
        message: err.message || "An unknown error occurred."
    }));

    const handleDismiss = () => {
        if (clearError) {
            clearError();
        }
    };

    // This function will handle both dismissing the modal and navigating
    const handleReconnect = (network) => {
        handleDismiss(); // Close the modal first
        // Navigate the user directly to the settings page to fix the connection
        navigate('/social-media'); 
    };

    return (
        <Modal
            isOpen={true} // Controlled by the existence of the 'error' prop
            onRequestClose={handleDismiss}
            className={styles.modalContent}
            overlayClassName={styles.modalOverlay}
            contentLabel="Error Notification"
            shouldCloseOnOverlayClick={true}
            shouldCloseOnEsc={true}
            aria={{ labelledby: "errorModalTitle" }}
        >
            <div className={styles.errorHeader}>
                <h2 id="errorModalTitle" className={styles.errorTitle}>
                    Action Required
                </h2>
            </div>

            <div className={styles.errorBody}>
                <ul className={styles.errorList}>
                    {errorsToDisplay.map((err, index) => {
                        console.log(`[ErrorDisplayModal LOG 4] Mapping item at index ${index}. Current 'err' object:`, err);
                        // Check if this error code requires a reconnect action
                        const codeToCheck = err.code;
                        const reconnectCodes = [
                            ERROR_CODES.GMAIL_TOKEN_EXPIRED,
                            ERROR_CODES.MISSING_GMAIL_TOKENS,
                            ERROR_CODES.OAUTH_TOKEN_REVOKED,
                            ERROR_CODES.OAUTH_NOT_CONFIGURED,
                        ];
                        console.log(`[ErrorDisplayModal LOG 5] Checking if code '${codeToCheck}' (type: ${typeof codeToCheck}) is in the reconnect list:`, reconnectCodes);
                        const needsReconnect = reconnectCodes.includes(codeToCheck);
                        console.log(`[ErrorDisplayModal LOG 6] Result of '.includes()' check ('needsReconnect'):`, needsReconnect);
                        return (
                            <li key={index} className={styles.errorMessageItem}>
                                <span className={styles.errorMessageText}>
                                    {/* Prefer the official message from constants, but fall back to the dynamic one */}
                                    {ERROR_MESSAGES[err.code] || err.message}
                                </span>
                                {needsReconnect && (
                                    <button
                                        onClick={() => {
                                            // The 'network' can be determined or passed if needed,
                                            // but navigating to the main settings page is often sufficient.
                                            handleReconnect();
                                        }}
                                        className={`${styles.actionButton} ${styles.reconnectButton}`}
                                    >
                                        Go to Settings
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>

            <div className={styles.errorFooter}>
                <button
                    onClick={handleDismiss}
                    className={`${styles.actionButton} ${styles.dismissButton}`}
                >
                    Dismiss
                </button>
            </div>
        </Modal>
    );
}

export default ErrorDisplayModal;